import { useAuth } from "@mindlercare/auth-ui";
import {
  MindlerAPIError,
  ReactQueryV5,
  RouterOutput,
} from "@mindlercare/mindlerapi-client";
import { SlotMethod } from "@mindlercare/schema";
import { Button, Icon, useSnackbar } from "@mindlercare/ui-web";
import { defaultTheme } from "@mindlercare/ui-web/src/shared/constants";
import DialogActions from "@mui/material/DialogActions/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import { useMindlerApi } from "api/apiv2";
import { useChangeTitle } from "api/usePsychologistQueries";
import { useInvalidateSlots } from "components/booking/hooks/useInvalidateSlots";
import { useSelectedPsychologist } from "components/booking/hooks/useSelectedPsychologist";
import { Typography } from "components/typography";
import CONSTANTS from "core/constants";
import {
  addMinutes,
  format,
  fromUnixTime,
  getUnixTime,
  isAfter,
} from "date-fns";
import { getTemplate, getTemplateName } from "helpers/templates";
import { useTemplates } from "hooks/useTemplates";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { Slot, SlotApiRes, Template } from "../../../core/types";
import { useCloseDialog } from "./actions";
import { DeleteOrCancelSlotModal } from "./deleteOrCancelSlotModal";
import { EditSlotModal } from "./editSlotModal";
import { PaymentInfo } from "./paymentInfo";

const { useMutation, useQuery, useQueryClient } = ReactQueryV5;

export const SlotTitle = ({
  slot,
  bookingIntent,
  templateName,
  closeDialog,
  isBookingPatient,
  onSlotMethodUpdate,
}: {
  slot: Slot;
  bookingIntent?: RouterOutput["psychologists"]["bookingIntent"];
  isBookingPatient: boolean;
  templateName?: string;
  onSlotMethodUpdate: (slotMethod: SlotMethod) => void;
  closeDialog: () => void;
}) => {
  const { t } = useTranslation();
  const { data: templates } = useTemplates();
  const invalidateSlots = useInvalidateSlots();
  const closeDialogWithHistory = useCloseDialog(true);
  const textInput = useRef<HTMLInputElement>(null);

  const [overriddenSlot, setOverriddenSlot] = useState<Partial<Slot> | null>(
    null,
  );

  const showingSlot = {
    ...slot,
    ...overriddenSlot,
  };

  const template = getTemplate(showingSlot.templateId, templates);
  const title = getTemplateName(template?.name ?? "", t);

  const startsAt = fromUnixTime(showingSlot.startsAt);
  const endsAt = fromUnixTime(showingSlot.endsAt);

  const selectedPsychologist = useSelectedPsychologist();
  const { userId } = useAuth();
  const psychologistUserId = (selectedPsychologist?.user.userId ??
    userId) as number;

  const slotTitle = showingSlot.bookingName || title;

  const queryClient = useQueryClient();
  const api = useMindlerApi();

  const handleSaveTitle = async (title: string) => {
    setOverriddenSlot({
      // let's be optimistic and update already here (nice for UX) :-)
      bookingName: title,
    });
    queryClient.setQueriesData<SlotApiRes[]>(
      api.slots.psychologist.calendar.queryFilter(),
      (old) => {
        if (!old) return old;
        return old.map((s) =>
          s.slotId === slot.slotId ? { ...s, bookingName: title } : s,
        );
      },
    );
    await changeTitle({
      slotId: slot.slotId,
      slotTitle: title,
    });
    await invalidateSlots();
    if (textInput.current) textInput.current.blur();
  };
  const { data: isCouplesTherapyRevisit } = useQuery(
    api.slots.isRevisit.queryOptions(
      {
        slotId: slot.slotId,
      },
      {
        enabled:
          templateName === CONSTANTS.SLOT_TEMPLATES.COUPLES_THERAPY &&
          slot.status !== CONSTANTS.BOOKING_STATUS.OPEN,
        refetchOnMount: false,
      },
    ),
  );

  const slotTitleText = isCouplesTherapyRevisit
    ? `${slotTitle} (${t("REVISIT")})`
    : slotTitle;

  const slotTimeText = `${format(
    startsAt,
    CONSTANTS.DEFAULT_TIME_FORMAT,
  )}-${format(endsAt, CONSTANTS.DEFAULT_TIME_FORMAT)}`;

  const { mutateAsync: updateSlot } = useMutation(
    api.slots.psychologist.move.mutationOptions(),
  );
  const { mutateAsync: changeTitle } = useChangeTitle();

  const { enqueueSnackbar } = useSnackbar();

  const [editSlotModalOpen, setEditSlotModalOpen] = useState(false);
  const [deleteOrCancelSlotModalOpen, setDeleteOrCancelSlotModalOpen] =
    useState(false);
  const meetingInTheFuture = isAfter(startsAt, new Date());
  const canDelete =
    (slot.status === "OPEN" && meetingInTheFuture) || !template?.isMeeting;
  const slotIsInBookedState =
    slot.status === "BOOKED" || slot.status === "UNAVAILABLE";
  const canCancel =
    Boolean(slot.patientId) && meetingInTheFuture && slotIsInBookedState;
  const showTrashCanIcon = canDelete || canCancel;
  return (
    <StyledDialogTitle id="dialog-title">
      <DialogHeaderContainer>
        <TitleRow>
          <DialogHeaderTextContainer>
            <Icon.Slot />{" "}
            <StyledSlotTitleText insensitive variant="h3">
              {slotTitleText}
            </StyledSlotTitleText>
            <Icon.Clock />
            <StyledSlotTime insensitive variant="h3">
              {slotTimeText}
            </StyledSlotTime>
            {slot.status === "OPEN" && (
              <StyledEditText
                insensitive
                variant="body-small"
                dataTestId="pw-edit-slot-button"
                underline
                onClick={() => setEditSlotModalOpen(true)}
              >
                {t("EDIT")}
              </StyledEditText>
            )}
            <PaymentInfo
              slot={slot}
              closeDialogWithHistory={() => closeDialogWithHistory()}
            />
          </DialogHeaderTextContainer>
          <ButtonWrapper>
            {showTrashCanIcon && (
              <DeleteOrCancelButtonWrapper>
                <Button
                  testId="pw-cancel-or-delete-slot"
                  variant="icon"
                  onClick={() => setDeleteOrCancelSlotModalOpen(true)}
                >
                  <Icon.Trash
                    width={defaultTheme.ICON_SIZE.SMALL}
                    color={defaultTheme.COLORS.GRAY_SYSTEM}
                  />
                </Button>
              </DeleteOrCancelButtonWrapper>
            )}
            <CloseDialog>
              <CloseIcon onClick={closeDialog} data-testid="close-icon">
                <Icon.Close />
              </CloseIcon>
            </CloseDialog>
          </ButtonWrapper>
        </TitleRow>
        <EditSlotModal
          slot={showingSlot}
          availableSlotMethods={bookingIntent?.slotMethods ?? []}
          open={editSlotModalOpen}
          fieldsToEdit={
            isBookingPatient
              ? ["slotMethod", "slotName", "slotTemplate"]
              : ["slotName", "slotTemplate"]
          }
          setModalOpen={setEditSlotModalOpen}
          onApplyEditSlot={async (editedSlot) => {
            if (editedSlot.newTemplateId) {
              const template = getTemplate(
                editedSlot.newTemplateId,
                templates,
              ) as unknown as Template;
              const newEndsAt = addMinutes(startsAt, template.defaultLength);
              const currentSlotAttributes = {
                ...showingSlot,
              };
              setOverriddenSlot({
                // optimistic update - let's fix the issue if there is an error
                endsAt: getUnixTime(newEndsAt),
                templateId: template.templateId,
                bookingName: editedSlot.editedSlotTitle,
              });
              try {
                await updateSlot({
                  psychologistUserId,
                  startsAt,
                  endsAt: newEndsAt,
                  slotId: slot.slotId,
                  newTemplateId: template.templateId,
                });
                if (editedSlot.editedSlotTitle) {
                  await handleSaveTitle(editedSlot.editedSlotTitle);
                }
                enqueueSnackbar({
                  text: t("SLOT_TYPE_UPDATED"),
                  isError: false,
                });
                closeDialog();
                await invalidateSlots();
              } catch (err: unknown) {
                setOverriddenSlot({
                  // reverting the optimistic update
                  ...currentSlotAttributes,
                });
                const messageToErrorMap: Record<string, string> = {
                  "Psychologist has overlapping slot":
                    "PSYCHOLOGIST_OVERLAPPING_SLOT_ERROR",
                };
                const mindlerErr = err as MindlerAPIError;
                const errorMessageCode: string | undefined =
                  messageToErrorMap[mindlerErr.message];
                const errorMessage = errorMessageCode
                  ? t(errorMessageCode)
                  : mindlerErr.message;
                enqueueSnackbar({
                  text: errorMessage,
                  isError: true,
                  length: "l",
                });
              }
            } else if (editedSlot.editedSlotTitle) {
              closeDialog();
              await handleSaveTitle(editedSlot.editedSlotTitle);
            } else if (editedSlot.newSlotMethod) {
              onSlotMethodUpdate(editedSlot.newSlotMethod);
            }
          }}
        />
        <DeleteOrCancelSlotModal
          open={deleteOrCancelSlotModalOpen}
          setModalOpen={setDeleteOrCancelSlotModalOpen}
          slot={{
            slotId: slot.slotId,
            psychologistUserId: slot.psychologistUserId,
            patientId: slot.patientId ?? null,
            templateName: slot.templateName,
          }}
        />
      </DialogHeaderContainer>
    </StyledDialogTitle>
  );
};

const DeleteOrCancelButtonWrapper = styled.div`
  align-items: center;
`;

const DialogHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0;
  color: ${({ theme }) => theme.COLORS.GRAY_BLACK};
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const StyledSlotTitleText = styled(Typography)`
  margin-left: 4px;
  margin-right: 10px;
`;

const StyledSlotTime = styled(Typography)`
  margin-left: 0px;
  padding-bottom: 0px;
`;

const StyledEditText = styled(Typography)`
  margin-left: 4px;
  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const CloseDialog = styled(DialogActions)`
  margin: 0;
  cursor: pointer;
`;

const CloseIcon = styled.span`
  display: flex;
`;

const StyledDialogTitle = styled(DialogTitle)`
  padding: 16px 33px 16px 40px;

  h2 {
    margin: 0;
    padding: 0;
  }
`;

const DialogHeaderTextContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.SPACING.XS};
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.SPACING.XS};
`;
